import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import axios from "axios";
import { getAllClientAccounts, getShippingCouriers } from "../../../../services/shipping";

const defaultRow = {
  id: null,
  name: "",
  courier: "",
  thirdPartyAccountId: "",
  thirdPartyAccountText: "",
  thirdPartyAccountType: "account",
  sortOrder: "",
};

export default function ShipMethodsTab({ instanceUniqueId }) {
  const [methods, setMethods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [instanceId, setInstanceId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [shippingCouriers, setShippingCouriers] = useState([]);

  const courierOptions = useMemo(() => Array.from(new Set([
    ...shippingCouriers.map(({ id }) => String(id || '').trim()).filter(Boolean),
    ...methods.map(({ courier }) => String(courier || '').trim()).filter(Boolean),
  ])), [methods, shippingCouriers]);

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const fetchInstanceId = async () => {
    if (!instanceUniqueId) return;
    const res = await axios.get(
      `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
      headers
    );
    setInstanceId(res.data?.id || null);
  };

  const fetchMethods = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(
      `${SERVER}/instances/${targetInstanceId}/ship-methods`,
      headers
    );
    setMethods((res.data?.methods || []).map((method) => ({
      ...method,
      thirdPartyAccountId: method.thirdPartyAccountId || "",
      thirdPartyAccountText: method.thirdPartyAccountText || "",
      thirdPartyAccountType: method.thirdPartyAccountText ? "freeform" : "account",
      sortOrder: method.sortOrder ?? "",
    })));
  };

  const fetchAccounts = async () => {
    const res = await getAllClientAccounts({ instanceUniqueId });
    setAccounts(res?.data || []);
  };

  const fetchCouriers = async () => {
    try {
      const response = await getShippingCouriers();
      setShippingCouriers(response?.couriers || []);
    } catch {
      setShippingCouriers([]);
    }
  };

  useEffect(() => {
    fetchInstanceId();
    fetchAccounts();
    fetchCouriers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    if (instanceId) fetchMethods(instanceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const addRow = () => {
    setMethods((prev) => [...prev, { ...defaultRow }]);
  };

  const updateRow = (index, updates) => {
    setMethods((prev) =>
      prev.map((row, rowIndex) => (rowIndex === index ? { ...row, ...updates } : row))
    );
  };

  const saveRow = async (row, index) => {
    if (!instanceId) return;
    const name = row.name.trim();
    const courier = row.courier.trim();
    if (!name || !courier) return;

    const payload = {
      name,
      courier,
      thirdPartyAccountId:
        courier === "Other" && row.thirdPartyAccountType === "account"
          ? row.thirdPartyAccountId || null
          : null,
      thirdPartyAccountText:
        courier === "Other" && row.thirdPartyAccountType === "freeform"
          ? row.thirdPartyAccountText?.trim() || null
          : null,
      sortOrder: row.sortOrder === "" || row.sortOrder === null ? null : Number(row.sortOrder),
    };

    if (payload.sortOrder !== null && !Number.isFinite(payload.sortOrder)) return;

    setSavingId(row.id || `new-${index}`);
    if (row.id) {
      await axios.put(
        `${SERVER}/instances/${instanceId}/ship-methods/${row.id}`,
        payload,
        headers
      );
    } else {
      const res = await axios.post(
        `${SERVER}/instances/${instanceId}/ship-methods`,
        payload,
        headers
      );
      updateRow(index, {
        id: res.data?.method?.id || row.id,
      });
    }
    setSavingId(null);
    fetchMethods(instanceId);
  };

  const removeRow = async (row, index) => {
    if (!instanceId) return;
    if (!row.id) {
      setMethods((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
      return;
    }
    await axios.delete(`${SERVER}/instances/${instanceId}/ship-methods/${row.id}`, headers);
    fetchMethods(instanceId);
  };

  const canSaveRow = (row) => {
    if (!row.name.trim() || !row.courier.trim()) return false;
    if (row.courier !== "Other") return true;
    if (row.thirdPartyAccountType === "freeform") {
      return !!row.thirdPartyAccountText?.trim();
    }
    return !!row.thirdPartyAccountId;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Ship Methods
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Define available shipping methods and couriers for this instance.
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
            Ship methods describe service levels (Ground, Air, Pickup) and the courier they use.
          </Typography>
        </Box>

        <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={addRow}>
          Add Method
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Ship Method Name</TableCell>
            <TableCell>Courier</TableCell>
            <TableCell>Order</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(methods || []).map((row, index) => (
            <React.Fragment key={row.id || `new-${index}`}>
              <TableRow hover>
                <TableCell sx={{ minWidth: 220 }}>
                  <TextField
                    fullWidth
                    value={row.name}
                    onChange={(e) => updateRow(index, { name: e.target.value })}
                    placeholder="e.g. Ground, Local Pickup"
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 180 }}>
                  <TextField
                    select
                    fullWidth
                    value={row.courier}
                    onChange={(e) =>
                      updateRow(index, {
                        courier: e.target.value,
                        thirdPartyAccountId: "",
                        thirdPartyAccountText: "",
                        thirdPartyAccountType: "account",
                      })
                    }
                    placeholder="Select courier"
                  >
                    <MenuItem value="">Select</MenuItem>
                    {courierOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell sx={{ width: 140 }}>
                  <TextField
                    type="number"
                    value={row.sortOrder}
                    onChange={(e) => updateRow(index, { sortOrder: e.target.value })}
                    inputProps={{ min: 0 }}
                    placeholder="1"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => saveRow(row, index)}
                    size="small"
                    disabled={!canSaveRow(row) || savingId === (row.id || `new-${index}`)}
                  >
                    <SaveOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton onClick={() => removeRow(row, index)} size="small">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
              {row.courier === "Other" && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
                      <RadioGroup
                        row
                        value={row.thirdPartyAccountType || "account"}
                        onChange={(e) =>
                          updateRow(index, {
                            thirdPartyAccountType: e.target.value,
                            thirdPartyAccountId: "",
                            thirdPartyAccountText: "",
                          })
                        }
                      >
                        <FormControlLabel
                          value="account"
                          control={<Radio size="small" />}
                          label="Third-party account"
                        />
                        <FormControlLabel
                          value="freeform"
                          control={<Radio size="small" />}
                          label="Free-form"
                        />
                      </RadioGroup>
                    </FormControl>
                    {row.thirdPartyAccountType !== "freeform" ? (
                      <TextField
                        select
                        fullWidth
                        label="Third Party Account"
                        value={row.thirdPartyAccountId}
                        onChange={(e) =>
                          updateRow(index, { thirdPartyAccountId: e.target.value })
                        }
                      >
                        <MenuItem value="">Select account</MenuItem>
                        {(accounts || []).map((account) => (
                          <MenuItem key={account.id} value={account.id}>
                            {account.accountName || account.AccountName || `Account ${account.id}`}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <TextField
                        fullWidth
                        label="Third Party Shipping Account"
                        value={row.thirdPartyAccountText}
                        onChange={(e) =>
                          updateRow(index, { thirdPartyAccountText: e.target.value })
                        }
                        placeholder="Enter account details"
                      />
                    )}
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
          {(methods || []).length === 0 && (
            <TableRow>
              <TableCell colSpan={4} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                No ship methods yet. Add your first one.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
